import { useState } from 'react';
import { Card, Text, Button } from '@fluentui/react-components';
import './Reports.css';

interface Report {
  id: string;
  name: string;
  description: string;
  generatedDate: string;
  fileSize: string;
}

const DEMO_REPORTS: Report[] = [
  {
    id: 'report-1',
    name: 'Monthly Usage Report - March 2026',
    description: 'Comprehensive usage metrics for all tenants',
    generatedDate: '2026-04-01T10:00:00Z',
    fileSize: '2.4 MB',
  },
  {
    id: 'report-2',
    name: 'Tenant Performance Report - March 2026',
    description: 'Performance analytics and optimization recommendations',
    generatedDate: '2026-03-31T18:00:00Z',
    fileSize: '1.8 MB',
  },
  {
    id: 'report-3',
    name: 'Security Audit Report - Q1 2026',
    description: 'Security events, anomalies, and compliance',
    generatedDate: '2026-03-28T09:30:00Z',
    fileSize: '3.2 MB',
  },
  {
    id: 'report-4',
    name: 'API Usage Report - March 2026',
    description: 'API endpoint usage and performance metrics',
    generatedDate: '2026-03-25T14:00:00Z',
    fileSize: '1.5 MB',
  },
  {
    id: 'report-5',
    name: 'Revenue Report - Q1 2026',
    description: 'Billing and revenue analytics by tier and tenant',
    generatedDate: '2026-03-20T08:00:00Z',
    fileSize: '0.9 MB',
  },
];

export default function ReportsPage() {
  const [reports] = useState<Report[]>(DEMO_REPORTS);
  const [reportType, setReportType] = useState<string>('usage');

  const reportTypes = [
    { value: 'usage', label: 'Usage Reports' },
    { value: 'performance', label: 'Performance Reports' },
    { value: 'security', label: 'Security Reports' },
    { value: 'billing', label: 'Billing Reports' },
  ];

  return (
    <div className="reports-container">
      <div className="page-header">
        <div>
          <h1>Reports</h1>
          <Text size={300}>View and generate system analytics and reports</Text>
        </div>
        <Button appearance="primary">Generate New Report</Button>
      </div>

      <Card className="report-filters">
        <div className="filter-group">
          <label>Report Type</label>
          <select
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
            className="report-select"
          >
            {reportTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>
      </Card>

      <div className="reports-grid">
        {reports.map((report) => (
          <Card key={report.id} className="report-card">
            <div className="report-icon">📊</div>
            <h3>{report.name}</h3>
            <Text size={300} className="report-description">
              {report.description}
            </Text>
            <div className="report-meta">
              <div>
                <Text size={200} className="meta-label">
                  Generated
                </Text>
                <Text size={300}>
                  {new Date(report.generatedDate).toLocaleDateString()}
                </Text>
              </div>
              <div>
                <Text size={200} className="meta-label">
                  Size
                </Text>
                <Text size={300}>{report.fileSize}</Text>
              </div>
            </div>
            <div className="report-actions">
              <Button
                appearance="primary"
                size="small"
                onClick={() => alert('Download report functionality coming soon')}
              >
                Download
              </Button>
              <Button
                appearance="secondary"
                size="small"
                onClick={() => alert('View report functionality coming soon')}
              >
                View
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <Card className="report-stats">
        <h3>Report Summary</h3>
        <div className="summary-grid">
          <div className="summary-item">
            <Text size={400} weight="bold" className="summary-value">
              {reports.length}
            </Text>
            <Text size={200}>Total Reports</Text>
          </div>
          <div className="summary-item">
            <Text size={400} weight="bold" className="summary-value">
              {(
                reports.reduce(
                  (sum, r) => sum + parseFloat(r.fileSize),
                  0
                )
              ).toFixed(1)}{' '}
              MB
            </Text>
            <Text size={200}>Total Size</Text>
          </div>
          <div className="summary-item">
            <Text size={400} weight="bold" className="summary-value">
              {new Date(
                Math.max(...reports.map((r) => new Date(r.generatedDate).getTime()))
              ).toLocaleDateString()}
            </Text>
            <Text size={200}>Latest Report</Text>
          </div>
        </div>
      </Card>
    </div>
  );
}
